/**
 * GlobalVoiceAssistant — the always-available "Jarvis" assistant.
 *
 * Mounted once in the app layout, it is available on every authenticated page.
 * It uses the backend 'global' context to:
 *   - navigate the user anywhere in their role's scope ("open token ledger"),
 *   - answer questions / give guidance, quoting live data ("how many papers…"),
 *   - run page actions registered via useVoiceActions ("download set 5"),
 *   - with confirm-before-destructive and undo support.
 *
 * It hides itself on auth/landing pages and whenever a page mounts its own
 * specialized workflow assistant (tracked via VoiceAssistantContext).
 */

import { useContext, useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import API from '../api/axios';
import useVoiceAssistant from '../hooks/useVoiceAssistant';
import VoiceAssistantUI from './VoiceAssistantUI';
import { useVoiceSlot } from '../context/VoiceAssistantContext';
import { getNavOptions, currentPageLabel } from '../utils/voiceNavConfig';

const HIDE_ON = ['/login', '/register', '/', '/set-password'];

export default function GlobalVoiceAssistant() {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { pageAssistantActive, getAction, listActions, actionsVersion } = useVoiceSlot();

  const role = user?.role;
  const navOptions = useMemo(() => getNavOptions(role), [role]);

  // Live, role-aware data summary so the assistant can answer data questions.
  const [dataSummary, setDataSummary] = useState({});
  const refreshSummary = useCallback(() => {
    if (!user) return;
    API.get('/voice-assistant/context')
      .then(({ data }) => setDataSummary(data?.summary || {}))
      .catch(() => setDataSummary({}));
  }, [user]);
  useEffect(() => { refreshSummary(); }, [refreshSummary]);

  // Confirm/undo state.
  const [pending, setPending] = useState(null); // { name, params, desc }
  const undoRef = useRef(null);                  // { undo, description }
  const [canUndo, setCanUndo] = useState(false);

  // Built-in global actions, available everywhere, plus page-registered ones.
  const builtinActions = useMemo(
    () => [
      {
        name: 'logout',
        description: 'log you out of the application',
        confirm: true,
        handler: () => { if (logout) logout(); },
      },
    ],
    [logout]
  );

  // Action metadata sent to the model (name + description + params only).
  // actionsVersion forces a refresh when pages (un)register actions.
  const actionMeta = useMemo(() => {
    const page = listActions().map((a) => ({
      name: a.name,
      description: a.description,
      params: a.params || [],
    }));
    const builtin = builtinActions.map((a) => ({ name: a.name, description: a.description, params: [] }));
    return [...page, ...builtin];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listActions, builtinActions, actionsVersion]);

  const resolveAction = useCallback(
    (name) => getAction(name) || builtinActions.find((a) => a.name === name),
    [getAction, builtinActions]
  );

  // Execute a resolved action and wire up undo.
  const executeAction = useCallback(
    async (name, params) => {
      const def = resolveAction(name);
      if (!def || typeof def.handler !== 'function') return;
      try {
        const out = await def.handler(params || {});
        if (out && typeof out.undo === 'function') {
          undoRef.current = { undo: out.undo, description: def.description };
          setCanUndo(true);
        } else {
          undoRef.current = null;
          setCanUndo(false);
        }
      } catch (e) {
        // Surface failures via the assistant voice.
        if (voiceRef.current?.speak) voiceRef.current.speak(`Sorry, I couldn't ${def.description}.`);
      }
    },
    [resolveAction]
  );

  const handleStateChange = useCallback(
    (updatedState) => {
      if (!updatedState) return;

      // Navigation
      if (updatedState.navigateBack) { navigate(-1); return; }
      if (updatedState.navigateTo) { navigate(updatedState.navigateTo); return; }

      // Confirmation flow
      if (updatedState.requestConfirm && updatedState.pendingActionName) {
        setPending({
          name: updatedState.pendingActionName,
          params: updatedState.pendingActionParams || {},
          desc: updatedState.pendingActionDesc || '',
        });
        return;
      }
      if (updatedState.clearPending && !updatedState.runAction) {
        setPending(null);
      }

      // Run an action (possibly the just-confirmed one)
      if (updatedState.runAction && updatedState.runAction.name) {
        if (updatedState.clearPending) setPending(null);
        executeAction(updatedState.runAction.name, updatedState.runAction.params);
        return;
      }

      // Undo
      if (updatedState.undoLast && undoRef.current?.undo) {
        try { undoRef.current.undo(); } catch (e) { /* noop */ }
        undoRef.current = null;
        setCanUndo(false);
      }
    },
    [navigate, executeAction]
  );

  const formState = useMemo(
    () => ({
      role,
      pathname: location.pathname,
      currentPageLabel: currentPageLabel(role, location.pathname),
      navOptions,
      dataContext: dataSummary,
      actions: actionMeta,
      pendingActionName: pending?.name || null,
      pendingActionParams: pending?.params || {},
      pendingActionDesc: pending?.desc || null,
      canUndo,
    }),
    [role, location.pathname, navOptions, dataSummary, actionMeta, pending, canUndo]
  );

  // Hidden on auth/landing pages and whenever a page-specific assistant is active.
  const visible = !!user && !pageAssistantActive && !HIDE_ON.includes(location.pathname);

  const voice = useVoiceAssistant({
    context: 'global',
    formState,
    onStateChange: handleStateChange,
    enabled: visible,
  });

  // Keep a ref to the voice API for use inside callbacks (error feedback).
  const voiceRef = useRef(voice);
  useEffect(() => { voiceRef.current = voice; }, [voice]);

  if (!visible) return null;

  return (
    <VoiceAssistantUI
      isListening={voice.isListening}
      isProcessing={voice.isProcessing}
      isSpeaking={voice.isSpeaking}
      isActive={voice.isActive}
      message={voice.assistantMessage}
      onStart={() => { refreshSummary(); voice.startListening(); }}
      onStop={voice.stopListening}
      error={voice.error}
      isSupported={voice.isSupported}
      continuousMode={voice.continuousMode}
      onToggleContinuousMode={voice.setContinuousMode}
      currentSuggestions={voice.currentSuggestions}
      workflowState={voice.workflowState}
      voiceLang={voice.voiceLang}
      onChangeVoiceLang={voice.setVoiceLang}
    />
  );
}
